import type { RouteLocationNormalized } from "vue-router";
import type { ModuleOptions } from "../types";
import type { AccessManager } from "./accessManager";

export function handleRouteNavigation(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  accessManager: AccessManager,
  config: ModuleOptions,
): boolean | string {
  // Extract permissions and roles from route metadata
  const routePermissions = to.meta?.permissions as
    | string
    | string[]
    | undefined;
  const routeRoles = to.meta?.roles as string | string[] | undefined;

  // If no permissions or roles are required, allow access
  if (!routePermissions && !routeRoles) {
    return true;
  }

  // Use our AccessManager to check if the user has the required access
  const hasAccess = accessManager.checkAccess(routePermissions, routeRoles);

  // If the user has access, allow navigation
  if (hasAccess) {
    return true;
  }

  // Handle unauthorized access based on configuration
  if (!config.redirectIfNotAllowed) {
    // Deny terminally: cancel, never redirect. Any redirect here replays every
    // global middleware, which can pick a destination this check denies again.
    // access-guard sends a role-less user to /audit-passengers and a Ville user
    // to /city, both of which require a role the user does not have, so the
    // ping-pong never settles and vue-router's own loop guard is dev-only.
    //
    // `false` lands in two different places, both known and both preferable to
    // a loop:
    //  - In-app navigation: vue-router cancels and swallows it. The click does
    //    nothing, with no toast and no message.
    //  - Cold load or hard refresh: because `ssr: false`, Nuxt takes the
    //    `!payload.serverRendered && isHydrating` branch in
    //    pages/runtime/plugins/router.js and calls showError with a 404
    //    "Page Not Found", which is the wrong status for a permission denial.
    // This repo has no error.vue, so that page carries no nav and no logout.
    // Fixing both properly means an error.vue with a way out plus a real 403,
    // or a toast on the in-app path. Out of scope here.
    return false;
  }

  // Redirect to the configured path or default to home
  return config.redirectIfNotAllowed || "/";
}
